import { api } from "@/lib/apiClient";
import type { MailAttachmentPayload } from "@/lib/mailAttachments";
import type { AuftragMini } from "@/lib/orders";

/**
 * Persistierter Status einer Anfrage (TA):
 * - draft    – Entwurf im Wizard, noch keine TA-Nummer
 * - ready    – finalisiert (TA-Nummer vergeben), aber noch nicht versendet
 * - sent     – an den Lieferanten versendet
 * - erledigt – abgeschlossen ohne Bestellung (z. B. nur Preis fürs Angebot)
 */
export type AnfrageStatus = "draft" | "ready" | "sent" | "erledigt";

export interface AnfragePosition {
  id?: number;
  typ?: "hero" | "freitext";
  auftrag_id: number;
  auftragsnummer?: string | null;
  auftragsnummer_display?: string | null;
  ta_nummer?: string | null;
  menge: number;
  einheit?: string | null;
  text: string;
  hero_nr?: string | null;
  hero_ean?: string | null;
  hero_unit?: string | null;
  angebot_preis?: number | null;
  angebot_waehrung?: string | null;
  angebot_gueltig_bis?: string | null;
  angebot_datei_url?: string | null;
  ek_preis?: number | null;
  vk_preis?: number | null;
  source_ma_position_id?: number | null;
}

export interface AnfrageTkLink {
  order_id: number;
  created_at?: string;
  created_by?: string;
  position_ids?: number[];
  bestellnummern?: string | null;
}

export interface AnfrageGroupSibling {
  id: number;
  supplier_name?: string | null;
  supplier_email?: string | null;
  status?: AnfrageStatus;
  subject?: string | null;
  created_at?: string;
}

export interface AnfrageSourceMa {
  id: number;
  ma_nummer?: string | null;
  title?: string | null;
}

export interface AnfrageListItem {
  id: number;
  status: AnfrageStatus;
  subject?: string | null;
  supplier_name?: string | null;
  supplier_email?: string | null;
  supplier_contact_name?: string | null;
  body?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  anfrage_group_id?: number | null;
  positionen?: AnfragePosition[];
  /** Aufträge, die über die Positionen verknüpft sind (für Wizard/Entwurf und Anzeige). */
  auftraege?: AuftragMini[];
  tk_links?: AnfrageTkLink[];
  group_siblings?: AnfrageGroupSibling[];
  source_ma?: AnfrageSourceMa | null;
  /** Offene (noch nicht bestätigte/verworfene) Antwort-Vorschläge aus dem Postfach. */
  pending_mail_count?: number;
  /** Bereits zugeordnete Antworten (Mail-Archiv). */
  answer_count?: number;
  last_answer_at?: string | null;
  last_sent_at?: string | null;
  erledigt_at?: string | null;
  erledigt_von?: string | null;
}

export interface AnfrageDetail extends AnfrageListItem {
  supplier_id?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_anrede?: string | null;
}

export interface SupplierRecipientIn {
  supplier_id?: string | number | null;
  supplier_name?: string | null;
  supplier_email?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_anrede?: string | null;
  supplier_contact_name?: string | null;
  template_id?: number | null;
}

export interface AnfrageDraftIn {
  supplier_id: string | number | null;
  supplier_name?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_anrede?: string | null;
  supplier_contact_name?: string | null;
  supplier_email?: string | null;
  template_id?: number | null;
  snippet_template_id?: number | null;
  kuerzel: string;
  auftraege: AuftragMini[];
  positionen: AnfragePosition[];
  notizen?: string | null;
  zusatz_empfaenger?: SupplierRecipientIn[];
  source_ma_id?: number | null;
}

export interface AnfrageDraftOut extends AnfrageDraftIn {
  id: number;
  status: AnfrageStatus;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface AnfrageComposeResult {
  subject: string;
  body: string;
  ta_nummern?: Record<string, string>;
}

export interface AnfrageFinalizeResult {
  anfrage_id: number;
  subject?: string;
  body?: string;
  supplier_email?: string;
  supplier_name?: string;
}

export interface AnfrageFinalizeMultiResult {
  anfrage_group_id?: number | null;
  results: AnfrageFinalizeResult[];
}

export interface AnfrageSnippetTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
}


export interface AnfrageMailReviewItem {
  anfrage_id: number;
  to: string;
  supplier_label?: string;
  subject: string;
  bodyHtml: string;
}

export interface AnfrageMailReviewItemResolved extends AnfrageMailReviewItem {
  bodyPlain: string;
}

export interface AnfrageGroupComparison {
  suppliers: Array<{
    anfrage_id: number;
    supplier_name?: string | null;
    status?: string | null;
  }>;
  comparison_rows: Array<{
    text?: string | null;
    menge?: number | null;
    einheit?: string | null;
    by_supplier?: Record<
      string,
      {
        ek_preis?: number | null;
        angebot_waehrung?: string | null;
      }
    >;
  }>;
}

export interface AnfrageMailThreadItem {
  kind?: string;
  branch?: string;
  party?: string | null;
  party_label?: string | null;
  subject?: string | null;
  preview?: string | null;
  sort_at?: string | null;
  has_attachments?: boolean;
  detail?: {
    ref_type?: string;
    ref_id?: number | string | null;
  };
}

export interface AnfrageMailCandidate {
  id: number;
  anfrage_id?: number;
  status?: string;
  from_name?: string | null;
  from_email?: string | null;
  subject?: string | null;
  snippet?: string | null;
  received_at?: string | null;
  match_reasons?: string[];
  matched_ta_nummern?: string[];
  match_score?: number | null;
  has_attachments?: boolean;
  attachments_summary?: Array<{
    id: string | number;
    name?: string | null;
    contentType?: string | null;
    size?: number | null;
  }>;
  graph_message_id?: string | null;
}

export interface AnfrageMailCandidateTarget {
  auftrag_id: number;
  auftragsnummer?: string | null;
  beschreibung?: string | null;
  sharepoint_folder_id?: string | null;
  ta_nummern?: string[];
}

export interface AnfrageMailContentDetail {
  subject?: string | null;
  body?: string | null;
  body_content_type?: string | null;
  from_label?: string | null;
  to_label?: string | null;
  date?: string | null;
  /** Hinweis, wenn nicht die Original-Mail, sondern ein gespeicherter Text angezeigt wird. */
  note?: string | null;
  /** true = Original aus Microsoft 365 geladen. */
  via_graph?: boolean;
  kind?: "sent" | "incoming";
}

export interface ConvertAnfragePayload {
  anfrage_id: number;
  position_ids: number[];
  target_auftrag_id?: number | null;
  position_edits: Array<{
    position_id: number;
    menge: number;
    einheit?: string | null;
    text: string;
  }>;
}

export interface ConvertAnfrageResult {
  order_draft_id?: number;
  anfrage_id?: number;
  /** @deprecated Frühere API – Bestellung wurde sofort finalisiert */
  order_ids?: Record<string, number>;
  /** @deprecated Frühere API – Bestellung wurde sofort finalisiert */
  bestellnummern?: Record<string, string>;
}

export interface ListAnfragenParams {
  status?: AnfrageStatus;
  q?: string;
  auftrag_id?: number;
  /** Nur nicht erledigte Anfragen (wird ignoriert, wenn `status` gesetzt ist). */
  open_only?: boolean;
}

export async function listAnfragen(params: ListAnfragenParams = {}): Promise<AnfrageListItem[]> {
  const usp = new URLSearchParams();
  if (params.status) usp.set("status", params.status);
  if (params.q?.trim()) usp.set("q", params.q.trim());
  if (params.auftrag_id != null) usp.set("auftrag_id", String(params.auftrag_id));
  if (params.open_only) usp.set("open_only", "true");
  const qs = usp.toString();
  return api.get<AnfrageListItem[]>(`/anfragen${qs ? `?${qs}` : ""}`);
}

export async function getAnfrage(id: number): Promise<AnfrageDetail> {
  return api.get<AnfrageDetail>(`/anfragen/${id}`);
}

/** Anfrage abschließen, ohne sie in eine Bestellung umzuwandeln. */
export async function closeAnfrage(id: number): Promise<{ status: AnfrageStatus }> {
  return api.post<{ ok: boolean; anfrage_id: number; status: AnfrageStatus }>(`/anfragen/${id}/close`, {});
}

/** Erledigte Anfrage wieder öffnen (Status vor dem Abschluss). */
export async function reopenAnfrage(id: number): Promise<{ status: AnfrageStatus }> {
  return api.post<{ ok: boolean; anfrage_id: number; status: AnfrageStatus }>(`/anfragen/${id}/reopen`, {});
}

export async function listAnfrageDrafts(): Promise<AnfrageDraftOut[]> {
  return listAnfragen({ status: "draft" }) as Promise<AnfrageDraftOut[]>;
}

export async function createAnfrageDraft(body: AnfrageDraftIn): Promise<AnfrageDraftOut> {
  return api.post<AnfrageDraftOut>("/anfragen/drafts", body);
}

export async function updateAnfrageDraft(id: number, body: AnfrageDraftIn): Promise<AnfrageDraftOut> {
  return api.patch<AnfrageDraftOut>(`/anfragen/drafts/${id}`, body);
}

export async function deleteAnfrageDraft(id: number): Promise<void> {
  return api.del(`/anfragen/drafts/${id}`);
}

export async function composeAnfrageMail(draft: AnfrageDraftIn): Promise<AnfrageComposeResult> {
  return api.post<AnfrageComposeResult>("/anfragen/compose", { draft });
}

export async function finalizeAnfrage(body: {
  draft: AnfrageDraftIn;
  anfrage_id?: number | null;
  mark_sent?: boolean;
}): Promise<AnfrageFinalizeResult> {
  return api.post<AnfrageFinalizeResult>("/anfragen/finalize", body);
}

export async function finalizeAnfrageMulti(body: {
  draft: AnfrageDraftIn;
  mark_sent?: boolean;
}): Promise<AnfrageFinalizeMultiResult> {
  return api.post<AnfrageFinalizeMultiResult>("/anfragen/finalize-multi", body);
}

export async function patchAnfrageMailContent(
  anfrageId: number,
  body: { subject: string; body: string; mark_sent?: boolean },
): Promise<void> {
  await api.patch(`/anfragen/${anfrageId}/mail-content`, body);
}

export async function sendAnfrageMail(
  anfrageId: number,
  body: {
    subject: string;
    body: string;
    html_body?: string;
    sharepoint_item_ids?: string[];
    attachments?: MailAttachmentPayload[];
  },
): Promise<void> {
  await api.post(`/anfragen/${anfrageId}/send`, body);
}

export async function sendAnfrageGroupMail(
  groupId: number,
  payload: {
    messages: Array<{ anfrage_id: number; subject: string; body: string; html_body?: string }>;
    sharepoint_item_ids?: string[];
    attachments?: MailAttachmentPayload[];
  },
): Promise<{ results?: Array<{ ok?: boolean }> }> {
  return api.post(`/anfragen/group/${groupId}/send`, payload);
}

export async function saveAnfragePositionAngebot(
  anfrageId: number,
  positionId: number,
  body: Record<string, unknown>,
): Promise<void> {
  await api.post(`/anfragen/${anfrageId}/positions/${positionId}/angebot`, body);
}

export async function getAnfrageGroupComparison(groupId: number): Promise<AnfrageGroupComparison> {
  return api.get<AnfrageGroupComparison>(`/anfragen/group/${groupId}/comparison`);
}

export async function listAnfrageSnippetTemplates(): Promise<AnfrageSnippetTemplate[]> {
  return api.get<AnfrageSnippetTemplate[]>("/anfragen/snippet-templates");
}

export async function convertAnfrageToOrder(body: ConvertAnfragePayload): Promise<ConvertAnfrageResult> {
  return api.post<ConvertAnfrageResult>("/orders/from-anfrage", body);
}

export async function getAnfrageMailCandidateCounts(): Promise<Record<string, number>> {
  const data = await api.get<{ counts?: Record<string, number> }>("/anfragen/me/mail-candidates/counts");
  return data?.counts || {};
}

/** Summe aller offenen Lieferanten-Antworten (Mail-Vorschläge) für Nav-Badge. */
export async function fetchAnfragenAnswerCount(): Promise<number> {
  const counts = await getAnfrageMailCandidateCounts();
  return Object.values(counts).reduce((sum, value) => sum + Number(value || 0), 0);
}

export function notifyAnfragenMailCandidatesChanged(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event("anfragen_mail_candidates_changed"));
}

export async function getAnfrageMailCandidates(anfrageId: number): Promise<{
  candidates?: AnfrageMailCandidate[];
  archive?: AnfrageMailCandidate[];
  thread?: AnfrageMailThreadItem[];
}> {
  return api.get(`/anfragen/${anfrageId}/mail-candidates`);
}

export async function getAnfrageMailCandidateTargets(anfrageId: number): Promise<AnfrageMailCandidateTarget[]> {
  const data = await api.get<{ targets?: AnfrageMailCandidateTarget[] }>(
    `/anfragen/${anfrageId}/mail-candidates/targets`,
  );
  return data?.targets || [];
}

export async function getAnfrageMailThread(anfrageId: number): Promise<AnfrageMailThreadItem[]> {
  const data = await api.get<{ items?: AnfrageMailThreadItem[] }>(`/anfragen/${anfrageId}/mail-thread`);
  return data?.items || [];
}

export async function getAnfrageMailContentDetail(
  anfrageId: number,
  params: { ref_type: string; ref_id?: string | number | null },
): Promise<AnfrageMailContentDetail> {
  const usp = new URLSearchParams({ ref_type: params.ref_type });
  if (params.ref_id != null && params.ref_id !== "") usp.set("ref_id", String(params.ref_id));
  return api.get<AnfrageMailContentDetail>(`/anfragen/${anfrageId}/mail-content-detail?${usp}`);
}

export async function dismissAnfrageMailCandidate(anfrageId: number, candidateId: number): Promise<void> {
  await api.post(`/anfragen/${anfrageId}/mail-candidates/${candidateId}/dismiss`, {});
}

export async function confirmAnfrageMailCandidate(
  anfrageId: number,
  candidateId: number,
  body: {
    sharepoint_targets: Array<{ auftrag_id: number }>;
    include_eml: boolean;
    selected_attachment_ids: string[];
    move_in_mailbox_to: string | null;
  },
): Promise<void> {
  await api.post(`/anfragen/${anfrageId}/mail-candidates/${candidateId}/confirm`, body);
}

export async function listAnfrageCandidateAttachments(
  anfrageId: number,
  candidateId: number,
): Promise<Array<{ id: string | number; name?: string; contentType?: string; size?: number }>> {
  const data = await api.get<{ attachments?: Array<{ id: string | number; name?: string; contentType?: string; size?: number }> }>(
    `/anfragen/${anfrageId}/mail-candidates/${candidateId}/attachments`,
  );
  return data?.attachments || [];
}

export async function searchHeroProductsForAnfrage(
  supplierId: string | number,
  q: string,
): Promise<Array<{ name: string; nr?: string; unit?: string; ean?: string }>> {
  const usp = new URLSearchParams({
    supplier_id: String(supplierId),
    q: q || "",
  });
  return api.get(`/orders/hero/products?${usp}`);
}
